import type { DraftCategory, DraftSection } from "./checklistDraft";

export type IssueTarget =
  | { kind: "details" }
  | { kind: "category"; categoryUid: string }
  | { kind: "section"; sectionUid: string }
  | { kind: "field"; sectionUid: string; fieldUid: string };

export type Issue = { target: IssueTarget; where: string; message: string };

const DETAILS_TARGET: IssueTarget = { kind: "details" };

const categoryName = (c: DraftCategory, i: number) => c.label.trim() || `Category ${i + 1}`;
const sectionName = (s: DraftSection, i: number) => s.label.trim() || `Section ${i + 1}`;

function categoryIssue(category: DraftCategory, ci: number, message: string): Issue {
  return {
    target: { kind: "category", categoryUid: category.uid },
    where: categoryName(category, ci),
    message,
  };
}

function sectionIssue(section: DraftSection, si: number, message: string): Issue {
  return { target: { kind: "section", sectionUid: section.uid }, where: sectionName(section, si), message };
}

function fieldIssue(section: DraftSection, si: number, fi: number, message: string): Issue {
  const field = section.fields[fi];
  return {
    target: { kind: "field", sectionUid: section.uid, fieldUid: field.uid },
    where: `${sectionName(section, si)} › ${field.label.trim() || `Field ${fi + 1}`}`,
    message,
  };
}

/** Mirrors validate_checklist_schema on the server, so problems surface before the round trip. */
export function validateDraft(name: string, categories: DraftCategory[]): Issue[] {
  const issues: Issue[] = [];
  if (!name.trim()) {
    issues.push({ target: DETAILS_TARGET, where: "Details", message: "Name is required" });
  }
  categories.forEach((category, ci) => {
    if (!category.label.trim()) issues.push(categoryIssue(category, ci, "Category name is required"));
    category.sections.forEach((section, si) => {
      if (!section.label.trim()) issues.push(sectionIssue(section, si, "Section name is required"));
      section.fields.forEach((field, fi) => {
        if (!field.label.trim()) issues.push(fieldIssue(section, si, fi, "Label is required"));
        if (field.type !== "select") return;
        const options = field.options.map((o) => o.trim());
        if (options.length === 0) {
          issues.push(fieldIssue(section, si, fi, "Add at least one option"));
        } else if (options.some((o) => !o)) {
          issues.push(fieldIssue(section, si, fi, "Options can't be blank"));
        } else if (new Set(options).size !== options.length) {
          issues.push(fieldIssue(section, si, fi, "Options must be unique"));
        }
      });
    });
  });
  return issues;
}

const SERVER_PATH = /^category (\d+)(?:, section (\d+))?(?:, field (\d+))?: (.+)$/;

/** Maps the server's "category N, section M, field K: ..." errors back onto the draft rows. */
export function issuesFromServer(error: unknown, categories: DraftCategory[]): Issue[] {
  if (!error || typeof error !== "object") return [];
  return Object.entries(error as Record<string, unknown>).flatMap(([key, value]) => {
    const messages = Array.isArray(value) ? value.map(String) : [String(value)];
    return messages.map((message): Issue => {
      const match = key === "schema" ? SERVER_PATH.exec(message) : null;
      const category = match ? categories[Number(match[1]) - 1] : undefined;
      if (match && category) {
        const si = match[2] ? Number(match[2]) - 1 : -1;
        const fi = match[3] ? Number(match[3]) - 1 : -1;
        const section = si >= 0 ? category.sections[si] : undefined;
        if (section && fi >= 0 && section.fields[fi]) return fieldIssue(section, si, fi, match[4]);
        if (section) return sectionIssue(section, si, match[4]);
        return categoryIssue(category, Number(match[1]) - 1, match[4]);
      }
      return { target: DETAILS_TARGET, where: key === "schema" ? "Template" : key, message };
    });
  });
}
